import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Edit2,
  KeyRound,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { SecretField } from '@components/certificates/SecretField';
import { ModalShell } from '@/pages/financial/financialShared';
import { correiosIntegrationService } from '@/services/correiosIntegrationService';
import { companyService } from '@/services/companyService';
import { formatDateTimeBR } from '@/pages/financial/financialUtils';
import { Company } from '@/types/company';
import {
  CORREIOS_BASE_URL,
  CorreiosAmbiente,
  CorreiosCredentialsTestResult,
  CorreiosIntegration,
} from '@/types/correiosIntegration';
import {
  CorreiosIntegrationFormState,
  brlFromCents,
  buildCorreiosPayload,
  credentialsWillResetToken,
  emptyCorreiosForm,
  formFromIntegration,
  formatCNPJ,
} from './correiosIntegration';

// Aba "Integração por empresa" do provider Correios (Configuração → Transporte →
// Transportadoras). Uma linha por empresa emitente: contrato, cartão de postagem e
// credenciais CWS próprios. As credenciais aparecem em texto claro por decisão — a aba
// só existe para quem tem shipping_integrations.read (ninguém por padrão, só admin).

interface Props {
  canWrite: boolean;
}

// ─── Modal de cadastro/edição ───────────────────────────────────

interface FormModalProps {
  initial: CorreiosIntegration | null;
  companies: Company[];
  /** empresas que já têm linha (não podem ser escolhidas num cadastro novo) */
  takenCompanyIds: Set<number>;
  onSave: (companyId: number, form: CorreiosIntegrationFormState) => Promise<void>;
  onClose: () => void;
}

const IntegrationFormModal: React.FC<FormModalProps> = ({ initial, companies, takenCompanyIds, onSave, onClose }) => {
  const [form, setForm] = useState<CorreiosIntegrationFormState>(
    initial ? formFromIntegration(initial) : emptyCorreiosForm(0),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof CorreiosIntegrationFormState>(key: K, value: CorreiosIntegrationFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // trocar o ambiente troca a base URL padrão (editável), a não ser que o usuário já
  // tenha digitado um host próprio
  const changeAmbiente = (ambiente: CorreiosAmbiente) => {
    setForm((prev) => {
      const wasDefault = prev.base_url.trim() === '' || prev.base_url.trim() === CORREIOS_BASE_URL[prev.ambiente];
      return { ...prev, ambiente, base_url: wasDefault ? CORREIOS_BASE_URL[ambiente] : prev.base_url };
    });
  };

  const willResetToken = credentialsWillResetToken(initial, form);

  const handleSubmit = async () => {
    setError(null);
    const { payload, error: validationError } = buildCorreiosPayload(form);
    if (!payload || validationError) {
      setError(validationError ?? 'Dados inválidos');
      return;
    }
    try {
      setSaving(true);
      await onSave(form.company_id, form);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const selectableCompanies = initial
    ? companies.filter((c) => c.company_id === initial.company_id)
    : companies.filter((c) => !takenCompanyIds.has(c.company_id));

  return (
    <ModalShell
      title={initial ? `Integração Correios — ${initial.company_name}` : 'Nova integração Correios'}
      icon={<ShieldCheck size={18} className="text-emerald-600" />}
      onClose={onClose}
      maxWidth="max-w-3xl"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-slate-500">
          Contrato e credenciais do <strong>CNPJ emitente</strong>: é a empresa da NF que escolhe esta linha na
          cotação, na pré-postagem/rótulo e no rastreio. Passo a passo para obter os dados no portal CWS em{' '}
          <code>LogisticaBackGo/docs/correios-setup.md</code>.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Empresa <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
              value={form.company_id}
              disabled={!!initial}
              onChange={(e) => set('company_id', Number(e.target.value))}
            >
              <option value={0}>Selecione...</option>
              {selectableCompanies.map((c) => (
                <option key={c.company_id} value={c.company_id}>
                  {c.name} — {formatCNPJ(c.document)}
                </option>
              ))}
            </select>
            {!initial && selectableCompanies.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">Todas as empresas já têm integração cadastrada.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ambiente</label>
            <select
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={form.ambiente}
              onChange={(e) => changeAmbiente(e.target.value as CorreiosAmbiente)}
            >
              <option value="producao">Produção</option>
              <option value="homologacao">Homologação</option>
            </select>
          </div>
          <Input
            label="Base URL"
            value={form.base_url}
            onChange={(e) => set('base_url', e.target.value)}
            placeholder={CORREIOS_BASE_URL[form.ambiente]}
            helperText="Vazio = padrão do ambiente"
          />
          <Input
            label="Usuário (login Meu Correios)"
            required
            value={form.usuario}
            onChange={(e) => set('usuario', e.target.value)}
            maxLength={50}
            autoComplete="off"
          />
          <Input
            label="Código de acesso à API (portal CWS)"
            required
            value={form.codigo_acesso_api}
            onChange={(e) => set('codigo_acesso_api', e.target.value)}
            maxLength={100}
            autoComplete="off"
            helperText="Não é a senha do site. Espaços são removidos ao salvar."
          />
          <Input
            label="Contrato"
            required
            value={form.contrato}
            onChange={(e) => set('contrato', e.target.value)}
            placeholder="9912345678"
            maxLength={20}
          />
          <Input
            label="Cartão de postagem"
            required
            value={form.cartao_postagem}
            onChange={(e) => set('cartao_postagem', e.target.value)}
            placeholder="0012345678"
            maxLength={20}
          />
          <Input
            label="DR (Superintendência Estadual)"
            value={form.dr}
            onChange={(e) => set('dr', e.target.value)}
            placeholder="72"
            helperText="SP = 72"
          />
          <Input
            label="Timeout (segundos)"
            value={form.timeout_seconds}
            onChange={(e) => set('timeout_seconds', e.target.value)}
            placeholder="30"
          />
          <Input
            label="Teto de valor declarado por objeto (R$)"
            value={form.declared_value_cap}
            onChange={(e) => set('declared_value_cap', e.target.value)}
            placeholder="10.000,00"
            helperText="Vazio = sem teto. Acima dele o seguro é limitado ao teto do contrato."
          />
          <Input
            label="Base URL do Rastro (opcional)"
            value={form.rastro_base_url}
            onChange={(e) => set('rastro_base_url', e.target.value)}
            placeholder="Vazio = mesma Base URL"
          />
          <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => set('enabled', e.target.checked)}
              className="rounded border-slate-300"
            />
            Ativa — desmarcar desliga cotação, envio e rastreio Correios desta empresa (sem apagar as credenciais)
          </label>
        </div>

        {willResetToken && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-xs">
            <KeyRound size={14} className="mt-0.5 shrink-0" />
            <span>
              Usuário, código de acesso, contrato, cartão ou ambiente mudaram: o token vigente será zerado ao
              salvar e a próxima cotação/envio autentica de novo.
            </span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

// ─── Detalhe (leitura, credenciais em texto claro) ───────────────

const IntegrationDetail: React.FC<{ row: CorreiosIntegration; testResult?: CorreiosCredentialsTestResult }> = ({
  row,
  testResult,
}) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50 rounded-lg p-3">
    <SecretField label="Usuário" value={row.usuario} />
    <SecretField label="Código de acesso à API" value={row.codigo_acesso_api} />
    <SecretField label="Contrato" value={row.contrato} />
    <SecretField label="Cartão de postagem" value={row.cartao_postagem} />
    <div className="text-xs text-slate-600 sm:col-span-2 space-y-1">
      <div>
        Base URL: <span className="font-mono">{row.base_url}</span>
        {row.rastro_base_url && (
          <>
            {' '}· Rastro: <span className="font-mono">{row.rastro_base_url}</span>
          </>
        )}
      </div>
      <div>
        DR: {row.dr ?? '—'} · Timeout: {row.timeout_seconds}s · Teto valor declarado:{' '}
        {row.declared_value_cap_cents != null ? `R$ ${brlFromCents(row.declared_value_cap_cents)}` : 'sem teto'}
      </div>
      <div>
        Token válido até: {formatDateTimeBR(row.token_expira_em)}
        {row.renewal_in_progress_at && (
          <span className="text-amber-700"> · renovação em andamento desde {formatDateTimeBR(row.renewal_in_progress_at)}</span>
        )}
      </div>
      <div>
        Atualizado {formatDateTimeBR(row.updated_at)}
        {row.updated_by ? ` por ${row.updated_by}` : ''}
      </div>
    </div>
    {testResult && (
      <div
        className={`sm:col-span-2 flex items-start gap-2 rounded-lg border p-2 text-xs ${
          testResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-700'
        }`}
      >
        {testResult.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <XCircle size={14} className="mt-0.5 shrink-0" />}
        <span>
          {testResult.message}
          {testResult.expira_em ? ` (token de teste válido até ${formatDateTimeBR(testResult.expira_em)})` : ''}
        </span>
      </div>
    )}
  </div>
);

// ─── Painel ─────────────────────────────────────────────────────

export const CorreiosIntegrationPanel: React.FC<Props> = ({ canWrite }) => {
  const [rows, setRows] = useState<CorreiosIntegration[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<{ open: boolean; row: CorreiosIntegration | null }>({ open: false, row: null });
  const [busyCompany, setBusyCompany] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<Record<number, CorreiosCredentialsTestResult>>({});
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, comps] = await Promise.all([correiosIntegrationService.list(), companyService.getCompanies()]);
      setRows(list);
      setCompanies(comps);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar as integrações Correios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (companyId: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });

  const save = async (companyId: number, form: CorreiosIntegrationFormState) => {
    const { payload, error: validationError } = buildCorreiosPayload(form);
    if (!payload) throw new Error(validationError ?? 'Dados inválidos');
    const saved = await correiosIntegrationService.upsert(companyId, payload);
    setRows((prev) => {
      const others = prev.filter((r) => r.company_id !== saved.company_id);
      return [...others, saved].sort((a, b) => a.company_id - b.company_id);
    });
    setTestResults((prev) => {
      const next = { ...prev };
      delete next[companyId];
      return next;
    });
    setNotice(`Integração da empresa ${saved.company_name} salva.`);
  };

  const test = async (row: CorreiosIntegration) => {
    setBusyCompany(row.company_id);
    setError(null);
    try {
      const result = await correiosIntegrationService.testCredentials(row.company_id);
      setTestResults((prev) => ({ ...prev, [row.company_id]: result }));
      setExpanded((prev) => new Set(prev).add(row.company_id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao testar as credenciais');
    } finally {
      setBusyCompany(null);
    }
  };

  const resetToken = async (row: CorreiosIntegration) => {
    if (!window.confirm(`Zerar o token Correios da empresa ${row.company_name}? A próxima cotação/envio autentica de novo.`)) return;
    setBusyCompany(row.company_id);
    setError(null);
    try {
      await correiosIntegrationService.resetToken(row.company_id);
      setRows((prev) =>
        prev.map((r) => (r.company_id === row.company_id ? { ...r, token_expira_em: null, renewal_in_progress_at: null } : r)),
      );
      setNotice(`Token da empresa ${row.company_name} zerado.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao zerar o token');
    } finally {
      setBusyCompany(null);
    }
  };

  const remove = async (row: CorreiosIntegration) => {
    if (
      !window.confirm(
        `Excluir a integração Correios da empresa ${row.company_name}? A empresa deixa de cotar, enviar e rastrear pelos Correios até um novo cadastro. Para pausar sem perder as credenciais, prefira desmarcar "Ativa".`,
      )
    )
      return;
    setBusyCompany(row.company_id);
    setError(null);
    try {
      await correiosIntegrationService.remove(row.company_id);
      setRows((prev) => prev.filter((r) => r.company_id !== row.company_id));
      setNotice(`Integração da empresa ${row.company_name} excluída.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir a integração');
    } finally {
      setBusyCompany(null);
    }
  };

  const takenCompanyIds = new Set(rows.map((r) => r.company_id));
  const missingCompanies = companies.filter((c) => !takenCompanyIds.has(c.company_id));

  return (
    <div className="space-y-3">
      {editing.open && (
        <IntegrationFormModal
          initial={editing.row}
          companies={companies}
          takenCompanyIds={takenCompanyIds}
          onSave={save}
          onClose={() => setEditing({ open: false, row: null })}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500 max-w-2xl">
          Contrato Correios <strong>por empresa emitente</strong>. NF de empresa sem linha ativa aqui não cota nem
          envia pelos Correios; o rastreio da empresa também para.
        </p>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} className="p-1.5 text-slate-400 hover:text-slate-600" title="Atualizar">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
          {canWrite && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setEditing({ open: true, row: null })}
              disabled={companies.length > 0 && missingCompanies.length === 0}
              className="flex items-center gap-1"
            >
              <Plus size={14} /> Nova integração
            </Button>
          )}
        </div>
      </div>

      {notice && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-emerald-800 text-xs">
          <CheckCircle2 size={14} />
          <span>{notice}</span>
          <button type="button" className="ml-auto text-emerald-700" onClick={() => setNotice(null)}>
            ×
          </button>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-2 text-red-700 text-xs">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-6">
          <RefreshCw size={20} className="animate-spin text-slate-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-6 text-slate-400 text-sm italic">Nenhuma empresa com integração Correios cadastrada</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse min-w-[720px]">
            <thead>
              <tr className="bg-slate-100 border-b border-slate-200">
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Empresa</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Ambiente</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Contrato</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Cartão</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Token válido até</th>
                <th className="px-3 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Ativa</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const busy = busyCompany === row.company_id;
                return (
                  <React.Fragment key={row.company_id}>
                    <tr className="border-b border-slate-200 bg-white hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <button type="button" className="text-left" onClick={() => toggle(row.company_id)}>
                          <div className="font-medium text-slate-800">{row.company_name || `Empresa #${row.company_id}`}</div>
                          <div className="text-xs text-slate-500 font-mono">{formatCNPJ(row.company_document)}</div>
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex text-xs rounded-full px-2 py-0.5 font-medium ${
                            row.ambiente === 'producao' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {row.ambiente === 'producao' ? 'Produção' : 'Homologação'}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-slate-700">{row.contrato}</td>
                      <td className="px-3 py-2 font-mono text-slate-700">{row.cartao_postagem}</td>
                      <td className="px-3 py-2 text-slate-600">{formatDateTimeBR(row.token_expira_em)}</td>
                      <td className="px-3 py-2 text-center">
                        {row.enabled ? (
                          <CheckCircle2 size={16} className="inline text-emerald-600" />
                        ) : (
                          <XCircle size={16} className="inline text-slate-400" />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => toggle(row.company_id)}
                            className="px-2 py-1 text-xs text-slate-600 hover:text-emerald-700"
                          >
                            {expanded.has(row.company_id) ? 'Ocultar' : 'Detalhes'}
                          </button>
                          {canWrite && (
                            <>
                              <button
                                type="button"
                                onClick={() => test(row)}
                                disabled={busy}
                                className="p-1.5 text-slate-400 hover:text-emerald-600 disabled:opacity-40"
                                title="Testar credenciais nos Correios"
                              >
                                {busy ? <RefreshCw size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditing({ open: true, row })}
                                disabled={busy}
                                className="p-1.5 text-slate-400 hover:text-emerald-600 disabled:opacity-40"
                                title="Editar"
                              >
                                <Edit2 size={15} />
                              </button>
                              <button
                                type="button"
                                onClick={() => resetToken(row)}
                                disabled={busy}
                                className="p-1.5 text-slate-400 hover:text-amber-600 disabled:opacity-40"
                                title="Zerar token (força nova autenticação)"
                              >
                                <KeyRound size={15} />
                              </button>
                              <button
                                type="button"
                                onClick={() => remove(row)}
                                disabled={busy}
                                className="p-1.5 text-slate-400 hover:text-red-500 disabled:opacity-40"
                                title="Excluir"
                              >
                                <Trash2 size={15} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded.has(row.company_id) && (
                      <tr className="border-b border-slate-200 bg-white">
                        <td colSpan={7} className="px-3 py-3">
                          <IntegrationDetail row={row} testResult={testResults[row.company_id]} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && missingCompanies.length > 0 && rows.length > 0 && (
        <p className="text-xs text-amber-700">
          Sem integração: {missingCompanies.map((c) => c.name).join(', ')} — NF dessas empresas não sai pelos Correios.
        </p>
      )}
    </div>
  );
};

export default CorreiosIntegrationPanel;
